import { type ChangeEvent, type ReactNode } from 'react';
import {
    Calendar,
    Input,
    type CalendarChangeEvent,
} from '@ics-portal/guardian';
import { type Maybe } from '@models/Maybe';

export type AttributeFieldType = 'DATE' | 'DATETIME' | 'STRING' | 'NUMBER';

const VALID_ATTRIBUTE_FIELD_TYPES = new Set<string>([
    'DATE',
    'DATETIME',
    'STRING',
    'NUMBER',
]);

export function isValidAttributeFieldType(
    value: unknown,
): value is AttributeFieldType {
    return typeof value === 'string' && VALID_ATTRIBUTE_FIELD_TYPES.has(value);
}

interface CalendarFieldConfig {
    kind: 'calendar';
    showTime: boolean;
    formatStr: string;
}

interface TextFieldConfig {
    kind: 'text';
    htmlType: 'text' | 'number';
}

type FieldConfig = CalendarFieldConfig | TextFieldConfig;

const ATTRIBUTE_FIELD_CONFIG: Record<AttributeFieldType, FieldConfig> = {
    STRING: { kind: 'text', htmlType: 'text' },
    NUMBER: { kind: 'text', htmlType: 'number' },
    DATE: { kind: 'calendar', showTime: false, formatStr: 'yyyy-MM-dd' },
    DATETIME: {
        kind: 'calendar',
        showTime: true,
        formatStr: "yyyy-MM-dd'T'HH:mm:ss",
    },
};

export function isCalendarFieldType(fieldType: AttributeFieldType): boolean {
    return ATTRIBUTE_FIELD_CONFIG[fieldType].kind === 'calendar';
}

function isCalendarConfig(config: FieldConfig): config is CalendarFieldConfig {
    return config.kind === 'calendar';
}

interface TypeBasedFieldProps {
    fieldType: AttributeFieldType;
    name?: string;
    label?: string;
    placeholder?: string;
    className?: string;
    value?: Maybe<string | Date>;
    onCalendarChange?: (event: CalendarChangeEvent) => void;
    onTextChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    maxLength?: number;
    disabled?: boolean;
}

export function TypeBasedField({
    fieldType,
    name,
    label,
    placeholder,
    className,
    value,
    onCalendarChange,
    onTextChange,
    onBlur,
    maxLength,
    disabled,
}: TypeBasedFieldProps): ReactNode {
    const config = ATTRIBUTE_FIELD_CONFIG[fieldType];
    if (!config) return null;

    if (isCalendarConfig(config)) {
        return (
            <Calendar
                name={name}
                label={label}
                placeholder={placeholder}
                className={className}
                value={value as Maybe<Date>}
                onChange={onCalendarChange}
                onBlur={onBlur}
                showTime={config.showTime}
                formatStr={config.formatStr}
                appendTo={document.body}
                disabled={disabled}
            />
        );
    }

    return (
        <Input
            name={name ?? ''}
            label={label}
            placeholder={placeholder}
            className={className}
            value={value as string | undefined}
            onChange={onTextChange}
            onBlur={onBlur}
            type={config.htmlType}
            maxLength={maxLength}
            disabled={disabled}
        />
    );
}
